import { z } from 'zod';
import { MaterialListStatusSchema, MaterialQuoteSourceSchema, } from '../lib/enums';
import { zIsoDateTime, zMoneyNonNeg, zQtyNonNeg, zRateNonNeg, zUuid, } from './common';
/**
 * Materials DTOs — lists of what a job needs, and where to buy them.
 *
 * A materials list belongs to a contractor. It may be bound to an estimate
 * (while bidding), to a project (once won), to a single project ASSIGNMENT
 * (one subcontractor's portion), or to nothing at all. The binding is what
 * decides who can read it, and it is enforced in SQL by RLS, not here.
 *
 * Structural laws these shapes enforce — the same ones as schemas/projects.ts,
 * for the same reason:
 *
 *  - The SUB-facing shapes (`SubMaterial*`) are declared LITERALLY, never
 *    derived from the contractor shapes via .omit()/.pick(). A new field on a
 *    contractor DTO must therefore be added to the sub DTO ON PURPOSE — it can
 *    never leak in by inheritance. Every request shape is .strict().
 *
 *  - No sub-facing shape has a key for a client, an estimate, a markup or
 *    margin, another subcontractor, or a user identity. There is nothing to
 *    filter out, because there is no field to put it in.
 *
 *  - Vendor PRICES are deliberately visible to a subcontractor. A sub buying
 *    its own materials needs to know what they cost and where they are
 *    cheapest; that is the feature. What a sub must never see is what the
 *    contractor CHARGES for them — so cost crosses the seam and markup does
 *    not. `materialsMarkup` lives on the estimate DTO, never here.
 *
 * Comparison totals are always DERIVED (see lib/basket.ts) and never stored;
 * quotes are stored observations, like `estimate_lines.unitCost`.
 */
// ---------------------------------------------------------------- contractor
export const MaterialVendorSchema = z.object({
    id: zUuid,
    /** Stable adapter key: `library`, `1build`, … */
    key: z.string().min(1).max(40),
    name: z.string().min(1).max(160),
    /** Human label for WHERE this vendor is, e.g. "Duluth, MN". */
    locationLabel: z.string().max(160).nullable(),
});
export const MaterialListItemSchema = z.object({
    id: zUuid,
    listId: zUuid,
    position: z.number().int().min(0),
    description: z.string().min(1).max(400),
    qty: zQtyNonNeg,
    unit: z.string().min(1).max(40),
    /** Provenance into the company's material library, when it came from there. */
    sourceMaterialId: zUuid.nullable(),
    notes: z.string().max(2000).nullable(),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const MaterialListSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(200),
    status: MaterialListStatusSchema,
    /** Bindings. All nullable: a list may stand alone while a job is being bid. */
    estimateId: zUuid.nullable(),
    projectId: zUuid.nullable(),
    assignmentId: zUuid.nullable(),
    /** Crew of people (0029) this list belongs to; always inside `projectId`. */
    crewId: zUuid.nullable(),
    /** Copied label of the crew this list is scoped to, when it is scoped. */
    assignmentLabel: z.string().max(160).nullable(),
    notes: z.string().max(4000).nullable(),
    itemCount: z.number().int().min(0),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
// --------------------------------------------------------------- comparison
/**
 * Mirrors `BasketItemOutcome` in lib/basket.ts. `unit_mismatch` is distinct
 * from `not_quoted` on purpose: the vendor sells it, but in a unit we refuse to
 * convert silently — a fixable data problem the UI must name out loud.
 */
export const MaterialQuoteOutcomeSchema = z.enum(['priced', 'not_quoted', 'unit_mismatch']);
export const MaterialBasketLineSchema = z.object({
    itemId: zUuid,
    outcome: MaterialQuoteOutcomeSchema,
    unitPrice: zRateNonNeg.nullable(),
    lineTotal: zMoneyNonNeg.nullable(),
    asOf: zIsoDateTime.nullable(),
    quotedUnit: z.string().max(40).nullable(),
});
export const MaterialVendorBasketSchema = z.object({
    vendorId: zUuid,
    vendorName: z.string().min(1).max(160),
    /**
     * Sum over PRICED lines only. For a `complete` vendor this is the basket
     * total. For a partial vendor it covers fewer items and must never be shown
     * ranked against a complete vendor's total.
     */
    total: zMoneyNonNeg,
    itemsPriced: z.number().int().min(0),
    itemsTotal: z.number().int().min(0),
    complete: z.boolean(),
    missingItemIds: z.array(zUuid),
    mismatchedItemIds: z.array(zUuid),
    /** The basket is only as fresh as its oldest quote. */
    oldestAsOf: zIsoDateTime.nullable(),
    lines: z.array(MaterialBasketLineSchema),
});
export const MaterialComparisonSchema = z.object({
    itemsTotal: z.number().int().min(0),
    /** The one store to buy everything at. Null when nobody covers the whole list. */
    winner: MaterialVendorBasketSchema.nullable(),
    /** Saving against the next COMPLETE vendor. `'0.00'` is a genuine tie. */
    winnerSavings: zMoneyNonNeg.nullable(),
    complete: z.array(MaterialVendorBasketSchema),
    partial: z.array(MaterialVendorBasketSchema),
    vendors: z.array(MaterialVendorSchema),
});
export const MaterialListDetailSchema = z.object({
    list: MaterialListSchema,
    items: z.array(MaterialListItemSchema),
    /** Null until the list has been priced at least once. */
    comparison: MaterialComparisonSchema.nullable(),
});
// ----------------------------------------------------------------- requests
export const MaterialListQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
    q: z.string().trim().max(200).optional(),
    status: MaterialListStatusSchema.optional(),
    projectId: zUuid.optional(),
    estimateId: zUuid.optional(),
    crewId: zUuid.optional(),
})
    .strict();
/**
 * At most ONE binding may be supplied. A list bound to an assignment is
 * readable by that subcontractor, so the binding is a disclosure decision and
 * has to be unambiguous — never inferred from a combination.
 */
export const CreateMaterialListRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    estimateId: zUuid.optional(),
    projectId: zUuid.optional(),
    assignmentId: zUuid.optional(),
    /** A crew on `projectId` (required with it); the crew's foreman and members can then work the list. */
    crewId: zUuid.optional(),
    notes: z.string().max(4000).optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.crewId !== undefined && value.projectId === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['crewId'],
            message: 'crewId needs the projectId the crew belongs to',
        });
    }
    const bindings = [value.estimateId, value.projectId, value.assignmentId].filter((binding) => binding !== undefined);
    if (bindings.length > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'a list binds to at most one of estimateId, projectId or assignmentId — ' +
                'the binding decides who can read it',
        });
    }
});
export const UpdateMaterialListRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    status: MaterialListStatusSchema.optional(),
    notes: z.string().max(4000).nullable().optional(),
})
    .strict();
export const MaterialItemWriteSchema = z
    .object({
    description: z.string().trim().min(1).max(400),
    qty: zQtyNonNeg,
    unit: z.string().trim().min(1).max(40),
    sourceMaterialId: zUuid.optional(),
    notes: z.string().max(2000).optional(),
})
    .strict();
export const CreateMaterialItemsRequestSchema = z
    .object({ items: z.array(MaterialItemWriteSchema).min(1).max(500) })
    .strict();
export const UpdateMaterialItemRequestSchema = z
    .object({
    description: z.string().trim().min(1).max(400).optional(),
    qty: zQtyNonNeg.optional(),
    unit: z.string().trim().min(1).max(40).optional(),
    notes: z.string().max(2000).nullable().optional(),
    position: z.number().int().min(0).optional(),
})
    .strict();
/**
 * Manually record what a vendor charges. This is the Phase-A pricing path and
 * stays useful after vendor adapters exist — a contractor with negotiated pro
 * pricing beats any public list price, and only they know it.
 */
export const RecordMaterialQuoteRequestSchema = z
    .object({
    vendorId: zUuid,
    unit: z.string().trim().min(1).max(40),
    unitPrice: zRateNonNeg,
    sku: z.string().max(80).optional(),
})
    .strict();
/**
 * Price a whole list. `vendorIds` restricts the run; omitted means every vendor
 * available to this company. Phase A resolves against the material library only.
 */
export const PriceMaterialListRequestSchema = z
    .object({ vendorIds: z.array(zUuid).max(20).optional() })
    .strict();
/**
 * Import a list into an estimate as `kind: 'material'` lines. The estimate must
 * be a draft — the existing draft-lock trigger is the authority, this is only
 * the friendly refusal.
 */
export const ImportMaterialListRequestSchema = z
    .object({
    estimateId: zUuid,
    /** Append into this section; omitted creates one named after the list. */
    sectionId: zUuid.optional(),
    /**
     * Which vendor's prices to snapshot as line costs. Omitted uses the winning
     * vendor from the last comparison; if there is no winner the import is
     * refused rather than guessing.
     */
    vendorId: zUuid.optional(),
})
    .strict();
// ===========================================================================
// SUB-FACING SHAPES
//
// Declared literally. Do NOT derive these from the contractor shapes above,
// and do NOT add a key here without deciding, deliberately, that a
// subcontractor may see it. There is no client, no estimate, no markup, no
// other subcontractor, and no user identity anywhere below.
//
// Vendor cost IS present and that is intentional (see the file header): a sub
// buying its own materials needs to know what they cost. What the contractor
// charges for them is a different number and lives on the estimate.
// ===========================================================================
export const SubMaterialItemSchema = z
    .object({
    id: zUuid,
    position: z.number().int().min(0),
    description: z.string().min(1).max(400),
    qty: zQtyNonNeg,
    unit: z.string().min(1).max(40),
    notes: z.string().max(2000).nullable(),
})
    .strict();
export const SubMaterialListSummarySchema = z
    .object({
    id: zUuid,
    name: z.string().min(1).max(200),
    status: MaterialListStatusSchema,
    /** The job this list is for — a name only, never the client behind it. */
    projectName: z.string().min(1).max(200),
    contractorName: z.string().min(1).max(160),
    itemCount: z.number().int().min(0),
    updatedAt: zIsoDateTime,
})
    .strict();
export const SubMaterialListDetailSchema = z
    .object({
    id: zUuid,
    name: z.string().min(1).max(200),
    status: MaterialListStatusSchema,
    projectName: z.string().min(1).max(200),
    contractorName: z.string().min(1).max(160),
    items: z.array(SubMaterialItemSchema),
    /** Where to buy MY list. Cost only — never a markup, never a client price. */
    comparison: MaterialComparisonSchema.nullable(),
})
    .strict();
/**
 * Declared literally, NOT `MaterialItemWriteSchema.omit(...)`. Deriving it
 * would mean a new contractor-side write field silently became writable by a
 * subcontractor. Note the absence of `sourceMaterialId`: the library it would
 * point into belongs to the contractor, and a sub has no business addressing
 * rows in it.
 */
export const SubMaterialItemWriteSchema = z
    .object({
    description: z.string().trim().min(1).max(400),
    qty: zQtyNonNeg,
    unit: z.string().trim().min(1).max(40),
    notes: z.string().max(2000).optional(),
})
    .strict();
export const CreateSubMaterialItemsRequestSchema = z
    .object({ items: z.array(SubMaterialItemWriteSchema).min(1).max(200) })
    .strict();
/**
 * What a sub may change on its own list item. Deliberately NOT `position` — the
 * ordering of a checklist is the contractor's, same rule as project work items.
 */
export const UpdateSubMaterialItemRequestSchema = z
    .object({
    description: z.string().trim().min(1).max(400).optional(),
    qty: zQtyNonNeg.optional(),
    unit: z.string().trim().min(1).max(40).optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
// ------------------------------------------------------------------ exports
export const MaterialQuoteSchema = z.object({
    itemId: zUuid,
    vendorId: zUuid,
    unit: z.string().min(1).max(40),
    unitPrice: zRateNonNeg,
    sku: z.string().max(80).nullable(),
    source: MaterialQuoteSourceSchema,
    asOf: zIsoDateTime,
});
//# sourceMappingURL=materials.js.map